import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  PosthogEventType,
  type ProjectQuestion,
} from "@repo/app-types/client/client";
import type { Assessment } from "@repo/app-types/entities/assessment";
import type { PhoneNumberExtension } from "@repo/app-types/entities/phoneNumberExtension";
import type {
  JobOpeningHours,
  JobOpeningLocation,
  JobOpeningPay,
  Project,
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  InterviewAgentType,
  ProjectEmailDigestFrequency,
  ProjectMode,
  type ProjectSettings,
  projectSettingsDefaults,
} from "@repo/app-types/entities/project";
import { nanoid } from "nanoid";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import { jobTypeApi } from "../../api/entities/jobType.js";
import { allProjectsForCompanyEndpoint } from "../../api/entities/project.js";
import { TagType, buildListTag } from "../../api/helpers/tags.js";
import { api } from "../../api/redux.js";
import type { RootState } from "../../app/store.js";
import { captureAnalyticsEvent } from "../analytics/analyticsClient.js";

export const projectsAdapter = createEntityAdapter<ProjectWithInterviewCount>();

const initialState = projectsAdapter.getInitialState<{
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}>({
  status: "idle",
  error: null,
});

export const createProject = createAsyncThunk<
  ProjectWithInterviewCount & {
    assessment: Assessment | null;
    phone_number_extension: PhoneNumberExtension | null;
  },
  {
    name: string;
    incentive_quantity: number;
    questions: readonly ProjectQuestion[];
    short_id: string;
    settings?: ProjectSettings;
    mode: ProjectMode;
    job_description?: string;
    is_live?: boolean;
    phone_number_id?: string;
    assessment_id_to_duplicate?: string;
    owner_teammate_ids: readonly string[];
    job_type_id?: string;
    job_type_name?: string;
    location?: JobOpeningLocation;
    pay?: JobOpeningPay;
    hours?: JobOpeningHours;
    ext_id?: string;
    ext_provider?: string;
    duplicate_job_type?: boolean;
  },
  { rejectValue: string }
>("projects/createProject", async (project, thunkAPI) => {
  try {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.post("/projects", project);

    captureAnalyticsEvent(PosthogEventType.ProjectCreate, {
      project_id: response.data.id,
      project_name: response.data.name,
    });

    // Invalidate RTK Query cache for projects
    thunkAPI.dispatch(
      api.util.invalidateTags([buildListTag(TagType.Project, [])]),
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

interface CreateRecruitingProjectArgs {
  name: string;
  jobDescription?: string;
  phoneNumberId?: string;
  jobTypeId?: string;
  jobTypeName?: string;
  location?: JobOpeningLocation;
  pay?: JobOpeningPay;
  hours?: JobOpeningHours;
  extId?: string;
  extProvider?: string;
  shouldDuplicateJobType?: boolean;
}

export const createRecruitingProject = createAsyncThunk<
  ProjectWithInterviewCount & {
    assessment: Assessment | null;
    phone_number_extension: PhoneNumberExtension | null;
  },
  CreateRecruitingProjectArgs,
  { rejectValue: string }
>("projects/createRecruitingProject", async (args, thunkAPI) => {
  try {
    const {
      name,
      jobDescription,
      phoneNumberId,
      jobTypeId,
      jobTypeName,
      location,
      pay,
      hours,
      extId,
      extProvider,
      shouldDuplicateJobType,
    } = args;
    const teammateId = (thunkAPI.getState() as RootState).teammate.teammate?.id;

    if (!teammateId) {
      throw new Error("Teammate not found");
    }

    return await thunkAPI
      .dispatch(
        createProject({
          name,
          incentive_quantity: 0,
          questions: [],
          short_id: nanoid(),
          mode: ProjectMode.VOICE_AGENT,
          settings: {
            ...projectSettingsDefaults,
            interview_agent_type: InterviewAgentType.RecruitingInterviewer,
            send_email_digest_to_all_teammates: false,
            send_email_digest_to_project_owners: true,
            email_digest_frequency: ProjectEmailDigestFrequency.INSTANT,
          },
          is_live: true,
          job_description: jobDescription,
          phone_number_id: phoneNumberId,
          owner_teammate_ids: [teammateId],
          job_type_id: jobTypeId,
          job_type_name: jobTypeName,
          location,
          pay,
          hours,
          ext_id: extId,
          ext_provider: extProvider,
          duplicate_job_type: shouldDuplicateJobType,
        }),
      )
      .unwrap();
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

/** @deprecated Use `useAllProjectsForCompanyInfiniteQuery` instead. */
export const fetchAllProjects = createAsyncThunk<ProjectWithInterviewCount[]>(
  "projects/fetchAllProjects",
  async (_, thunkAPI) => {
    try {
      // Fetch the first page (or whatever is in cache)
      let action = thunkAPI.dispatch(
        allProjectsForCompanyEndpoint.initiate({ limit: 100 }),
      );
      let result = await action;

      // Fetch all remaining pages
      while (result.hasNextPage) {
        action = thunkAPI.dispatch(
          allProjectsForCompanyEndpoint.initiate(action.arg, {
            direction: "forward",
          }),
        );
        result = await action;
      }

      // Get all the data, or throw an error
      const data = await action.unwrap();

      return data.pages.flatMap((page) => page.projects);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchProjectById = createAsyncThunk(
  "projects/fetchProjectById",
  async (projectId: string, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get(`/projects/${projectId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const refreshProjectById = createAsyncThunk(
  "projects/refreshProjectById",
  async (projectId: string, thunkAPI) => {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.get(`/projects/${projectId}`, {
      signal: thunkAPI.signal,
    });
    return response.data;
  },
);

export const retryInferenceForProjectById = createAsyncThunk(
  "projects/retryInferenceForProjectById",
  async (projectId: string, thunkAPI) => {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.post(
      `/projects/${projectId}/retry-inference`,
      {
        signal: thunkAPI.signal,
      },
    );
    return response.data.data.project;
  },
);

const projectsSelectors = projectsAdapter.getSelectors(
  (state: RootState) => state.projects,
);

export const selectAllProjects = projectsSelectors.selectAll;
export const selectProjectById = projectsSelectors.selectById;
export const selectProjects = projectsSelectors.selectEntities;

export const updateProject = createAsyncThunk<
  Project,
  { projectId: string; changes: ProjectUpdate },
  { rejectValue: string }
>("projects/updateProject", async ({ projectId, changes }, thunkAPI) => {
  try {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.patch(`/projects/${projectId}`, changes);
    // Since we only update the diff in redux, we don't need to recalculate
    // the interview count when updating on the server.

    // Invalidate RTK Query cache for projects
    thunkAPI.dispatch(
      api.util.invalidateTags([buildListTag(TagType.Project, [])]),
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (projectId: string, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.delete(`/projects/${projectId}`);

      // Invalidate RTK Query cache for projects
      thunkAPI.dispatch(
        api.util.invalidateTags([buildListTag(TagType.Project, [])]),
      );

      return thunkAPI.fulfillWithValue(projectId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    projectAdded: projectsAdapter.addOne,
    projectRemoved: projectsAdapter.removeOne,
    projectUpdated: projectsAdapter.updateOne,
    setAllProjects: projectsAdapter.setAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProject.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.status = "succeeded";
        projectsAdapter.addOne(state, action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(fetchAllProjects.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllProjects.fulfilled, (state, action) => {
        state.status = "succeeded";
        projectsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchAllProjects.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(fetchProjectById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.status = "succeeded";
        projectsAdapter.upsertOne(state, action.payload);
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(refreshProjectById.fulfilled, (state, action) => {
        projectsAdapter.upsertOne(state, action.payload);
      })
      .addCase(retryInferenceForProjectById.fulfilled, (state, action) => {
        projectsAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateProject.pending, (state, action) => {
        state.status = "loading";
        // Optimistic update
        projectsAdapter.updateOne(state, {
          id: action.meta.arg.projectId,
          // Typecast because the ProjectUpdate type for `questions` is more permissive
          // than ProjectQuestion[], and we are optimistically updating with the fn arg,
          // rather than the return value of `updateProjectDb` (which maps the result to Project)
          changes: action.meta.arg.changes as Partial<Project>,
        });
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.status = "succeeded";
        projectsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(deleteProject.pending, (state, action) => {
        state.status = "loading";
        // Optimistic update
        console.log("deleting project", action.meta.arg);
        projectsAdapter.removeOne(state, action.meta.arg);
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.status = "succeeded";
        projectsAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      });
    builder.addMatcher(
      jobTypeApi.endpoints.updateJobType.matchFulfilled,
      (state, { payload: updatedJobType }) => {
        // Find every project whose job_type matches the updated ID
        const projectIds = Object.values(state.entities)
          .filter((p) => p?.job_type?.id === updatedJobType.id)
          .map((p) => p.id);

        projectsAdapter.updateMany(
          state,
          projectIds.map((id) => ({
            id,
            changes: { job_type: updatedJobType },
          })),
        );
      },
    );
  },
});

export const { projectAdded, projectRemoved, projectUpdated, setAllProjects } =
  projectsSlice.actions;
export default projectsSlice.reducer;
